import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
    ArrowLeft,
    Brain,
    CheckSquare,
    ClipboardCheck,
    FileText,
    MessageSquare,
    MoreVertical,
    Pin,
    RefreshCw,
    Search,
    Send,
    Sparkles,
    Trash2,
    UserX,
    Users,
    Wifi,
    WifiOff,
} from "lucide-react";
import { toast } from "sonner";
import { crud, whatsappMerkez } from "@/lib/api";
import useWebSocket from "@/lib/useWebSocket";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import WhatsAppInboxOnayDrawer from "@/components/whatsapp/WhatsAppInboxOnayDrawer";
import WhatsAppEvrakOnizlemeModal from "@/components/whatsapp/WhatsAppEvrakOnizlemeModal";
import WhatsAppInboxCrmPanel from "@/components/whatsapp/WhatsAppInboxCrmPanel";
import WhatsAppInboxComposer from "@/components/whatsapp/WhatsAppInboxComposer";
import "@/styles/whatsapp-inbox.css";

function sortKonusmalar(list) {
    return [...list].sort((a, b) => {
        const ta = a.son_tarih || "";
        const tb = b.son_tarih || "";
        if (tb !== ta) return tb.localeCompare(ta);
        return (b.okunmamis || 0) - (a.okunmamis || 0);
    });
}

function avatarInitials(name) {
    const parts = (name || "?").trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return (parts[0]?.[0] || "?").toUpperCase();
}

function konusmaBaslik(k) {
    if (!k) return "?";
    const bad = /^mbs\s*hukuk/i;
    if (k.gorunen_baslik && !bad.test(k.gorunen_baslik.trim())) return k.gorunen_baslik;
    if (k.muvekkil_adi && !bad.test(k.muvekkil_adi.trim())) return k.muvekkil_adi;
    if (k.is_grup) return k.grup_adi || "WhatsApp Grubu";
    return k.telefon_formatli || k.telefon || "?";
}

function formatListTime(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    if (now.toDateString() === d.toDateString()) {
        return d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
    }
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (yesterday.toDateString() === d.toDateString()) return "Dün";
    return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

function formatBubbleTime(iso) {
    if (!iso) return "";
    return new Date(iso).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
}

function formatDateSep(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    if (now.toDateString() === d.toDateString()) return "Bugün";
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (yesterday.toDateString() === d.toDateString()) return "Dün";
    return d.toLocaleDateString("tr-TR", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });
}

export default function WhatsAppInboxPanel({ busyKey, runBusy }) {
    const [konusmalar, setKonusmalar] = useState([]);
    const [seciliKonusmaKey, setSeciliKonusmaKey] = useState("");
    const [seciliMesajlar, setSeciliMesajlar] = useState({});
    const [selectMode, setSelectMode] = useState(false);
    const [yanitMetin, setYanitMetin] = useState("");
    const [etiketMetin, setEtiketMetin] = useState("");
    const [filtre, setFiltre] = useState("tumu");
    const [arama, setArama] = useState("");
    const [wsConnected, setWsConnected] = useState(false);
    const [baglaMuvekkil, setBaglaMuvekkil] = useState({});
    const [aiTaslaklar, setAiTaslaklar] = useState([]);
    const [aiMeta, setAiMeta] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);
    const [showTools, setShowTools] = useState(false);
    const [mobileChatOpen, setMobileChatOpen] = useState(false);
    const [crmOpen, setCrmOpen] = useState(false);
    const [evrakInceleme, setEvrakInceleme] = useState(null);
    const [evrakOnizleme, setEvrakOnizleme] = useState(null); // { inboxId, detail, atlaNeden, muvekkilId, dosyaId }
    const [dosyaArama, setDosyaArama] = useState("");
    const [dosyaAdaylar, setDosyaAdaylar] = useState([]);
    const [seciliDosyaId, setSeciliDosyaId] = useState("");
    const [manuelYukleme, setManuelYukleme] = useState(false);
    const [manuelEvrakDosya, setManuelEvrakDosya] = useState(null);
    const [onayDrawerOpen, setOnayDrawerOpen] = useState(false);
    const [onayDrawerInboxId, setOnayDrawerInboxId] = useState(null);
    const [varsayilanDosyaId, setVarsayilanDosyaId] = useState("");
    const [herZamanBuDosya, setHerZamanBuDosya] = useState(false);
    const messagesContainerRef = useRef(null);
    const shouldStickBottomRef = useRef(true);

    const konusmaKey = useCallback((k) => k?.konusma_anahtar || k?.telefon || "", []);

    const loadInbox = useCallback(async () => {
        const data = await whatsappMerkez.inboxKonusmalarGelismis({
            limit: 80,
            filtre,
            q: arama.trim() || undefined,
        });
        const rows = sortKonusmalar(data.konusmalar || []);
        setKonusmalar(rows);
        setSeciliKonusmaKey((prev) => {
            if (prev && rows.some((k) => konusmaKey(k) === prev)) return prev;
            return rows[0] ? konusmaKey(rows[0]) : "";
        });
    }, [konusmaKey, filtre, arama]);

    const evrakYukle = useCallback(
        async (inboxId) => {
            if (!inboxId) return;
            try {
                const res = await whatsappMerkez.inboxEvrakYukle(inboxId);
                if (res?.ok) {
                    if (res.queued || res.inbox_id) {
                        toast.info(
                            res.detail || "Evrak onay kuyruğuna alındı — önizleyip doğru dosyayı seçin.",
                        );
                        setOnayDrawerInboxId(inboxId);
                        setOnayDrawerOpen(true);
                        await loadInbox();
                        return;
                    }
                    if (res.evrak_id) {
                        toast.success(res.detail || "Evrak dosyaya yüklendi.");
                        await loadInbox();
                        return;
                    }
                    setOnayDrawerInboxId(inboxId);
                    setOnayDrawerOpen(true);
                    toast.info("Evrak inceleme ekranı açıldı.");
                    await loadInbox();
                } else {
                    // Otomatik yükleme başarısız — manuel inceleme modalı aç
                    setEvrakInceleme({
                        inboxId,
                        detail: res?.detail || "Evrak otomatik yüklenemedi.",
                        atlaNeden: res?.atla_nedeni || res?.detail || "",
                        muvekkilId: res?.muvekkil_id || null,
                        dosyaId: res?.dosya_id || null,
                        manualUploadRequired: !!res?.manual_upload_required,
                    });
                    setManuelEvrakDosya(null);
                }
            } catch (e) {
                const msg =
                    e?.response?.data?.detail ||
                    e?.response?.data?.atla_nedeni ||
                    e?.response?.data?.error ||
                    "Evrak otomatik yüklenemedi (güvenlik / eşleşme kontrolü).";
                // Hata detayını modalda göster
                setEvrakInceleme({
                    inboxId,
                    detail: msg,
                    atlaNeden: msg,
                    muvekkilId: e?.response?.data?.muvekkil_id || null,
                    dosyaId: e?.response?.data?.dosya_id || null,
                    manualUploadRequired: !!e?.response?.data?.manual_upload_required,
                });
                setManuelEvrakDosya(null);
            }
        },
        [loadInbox],
    );

    const aktifKonusma = useMemo(
        () => konusmalar.find((k) => konusmaKey(k) === seciliKonusmaKey) || null,
        [konusmalar, seciliKonusmaKey, konusmaKey],
    );

    const onayBekleyenToplam = useMemo(
        () => konusmalar.reduce((acc, k) => acc + (k.onay_bekleyen || 0), 0),
        [konusmalar],
    );

    useEffect(() => {
        if (!aktifKonusma) {
            setVarsayilanDosyaId("");
            setHerZamanBuDosya(false);
            return;
        }
        const bag = aktifKonusma.konusma_baglantisi || {};
        setVarsayilanDosyaId(bag.varsayilan_dosya_id ? String(bag.varsayilan_dosya_id) : "");
        setHerZamanBuDosya(!!bag.her_zaman_bu_dosya);
    }, [aktifKonusma?.konusma_anahtar, aktifKonusma?.konusma_baglantisi]);

    useEffect(() => {
        function onGlobalKey(e) {
            if (!(e.ctrlKey || e.metaKey)) return;
            if (e.key.toLowerCase() === "s" && onayBekleyenToplam > 0) {
                e.preventDefault();
                setOnayDrawerOpen(true);
            }
        }
        window.addEventListener("keydown", onGlobalKey);
        return () => window.removeEventListener("keydown", onGlobalKey);
    }, [onayBekleyenToplam]);

    async function handleVarsayilanDosyaKaydet() {
        if (!aktifKonusma || !varsayilanDosyaId) {
            toast.error("Varsayılan dosya seçin.");
            return;
        }
        await runBusy("inbox-varsayilan-dosya", async () => {
            const res = await whatsappMerkez.inboxKonusmaVarsayilanSet({
                konusma_anahtar: konusmaKey(aktifKonusma),
                dosya_id: Number(varsayilanDosyaId),
                her_zaman_bu_dosya: herZamanBuDosya,
            });
            if (res?.ok) {
                toast.success("Konuşma varsayılan dosyası kaydedildi.");
                await loadInbox();
            } else {
                toast.error(res?.detail || "Kaydedilemedi.");
            }
        });
    }

    const filtreliKonusmalar = useMemo(() => sortKonusmalar(konusmalar), [konusmalar]);

    const seciliIdler = useMemo(
        () => Object.entries(seciliMesajlar).filter(([, v]) => v).map(([id]) => Number(id)),
        [seciliMesajlar],
    );

    useEffect(() => {
        loadInbox().catch(() => toast.error("Gelen kutusu yüklenemedi."));
    }, [loadInbox]);

    const onWsMessage = useCallback(
        (data) => {
            const t = data?.type;
            if (t === "connected") {
                setWsConnected(true);
                return;
            }
            if (t === "inbox_message" || t === "inbox_conversation" || t === "inbox_meta") {
                loadInbox().catch(() => {});
            }
        },
        [loadInbox],
    );

    const { connected: wsLive } = useWebSocket("wa-inbox/", onWsMessage);

    useEffect(() => {
        setWsConnected(wsLive);
    }, [wsLive]);

    useEffect(() => {
        if (wsLive) return undefined;
        const timer = setInterval(() => {
            loadInbox().catch(() => {});
        }, 15000);
        return () => clearInterval(timer);
    }, [loadInbox, wsLive]);

    useEffect(() => {
        if (aktifKonusma) {
            setEtiketMetin(aktifKonusma.gorunen_baslik || "");
        }
    }, [aktifKonusma?.telefon, aktifKonusma?.gorunen_baslik]);

    const scrollMessagesToBottom = useCallback((behavior = "auto") => {
        const el = messagesContainerRef.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior });
    }, []);

    useEffect(() => {
        shouldStickBottomRef.current = true;
        requestAnimationFrame(() => scrollMessagesToBottom("auto"));
    }, [seciliKonusmaKey, scrollMessagesToBottom]);

    useEffect(() => {
        if (!shouldStickBottomRef.current) return;
        requestAnimationFrame(() => scrollMessagesToBottom("smooth"));
    }, [aktifKonusma?.mesajlar?.length, scrollMessagesToBottom]);

    function handleMessagesScroll() {
        const el = messagesContainerRef.current;
        if (!el) return;
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        shouldStickBottomRef.current = distanceFromBottom < 100;
    }

    async function secKonusma(key) {
        setSeciliKonusmaKey(key);
        setSeciliMesajlar({});
        setSelectMode(false);
        setMobileChatOpen(true);
        setCrmOpen(false);
        setYanitMetin("");
        try {
            await whatsappMerkez.inboxKonusmaOkundu(key);
            setKonusmalar((prev) =>
                sortKonusmalar(
                    prev.map((k) =>
                        konusmaKey(k) === key
                            ? {
                                  ...k,
                                  okunmamis: 0,
                                  mesajlar: k.mesajlar?.map((m) => ({ ...m, okundu_panel: true })),
                              }
                            : k,
                    ),
                ),
            );
        } catch {
            /* sessiz */
        }
    }

    function toggleMesaj(id, checked) {
        setSeciliMesajlar((p) => ({ ...p, [id]: checked }));
    }

    function toggleTumMesajlar(checked) {
        if (!aktifKonusma?.mesajlar) return;
        const next = {};
        if (checked) {
            aktifKonusma.mesajlar.forEach((m) => {
                next[m.id] = true;
            });
        }
        setSeciliMesajlar(next);
    }

    async function handleSeciliSil() {
        if (seciliIdler.length === 0) {
            toast.error("Silinecek mesaj seçin.");
            return;
        }
        if (!window.confirm(`${seciliIdler.length} mesaj silinsin mi?`)) return;
        await runBusy("inbox-sil-secili", async () => {
            const res = await whatsappMerkez.inboxSil(seciliIdler);
            toast.success(`${res.silinen || 0} mesaj silindi.`);
            setSelectMode(false);
            await loadInbox();
        });
    }

    async function handleKonusmaSil() {
        if (!aktifKonusma) return;
        if (!window.confirm(`"${aktifKonusma.gorunen_baslik}" konuşmasındaki tüm mesajlar silinsin mi?`)) return;
        await runBusy("inbox-sil-konusma", async () => {
            const res = await whatsappMerkez.inboxKonusmaSil(konusmaKey(aktifKonusma));
            toast.success(`${res.silinen || 0} mesaj silindi.`);
            setSeciliKonusmaKey("");
            setMobileChatOpen(false);
            await loadInbox();
        });
    }

    async function handleEslestirmeKaldir() {
        if (!aktifKonusma) return;
        const etiket = etiketMetin.trim() || aktifKonusma.gonderen_adi || aktifKonusma.telefon_formatli;
        await runBusy("inbox-eslestirme", async () => {
            const res = await whatsappMerkez.inboxEslestirmeKaldir(konusmaKey(aktifKonusma), etiket);
            toast.success(`Otomatik eşleşme kaldırıldı (${res.guncellenen_mesaj || 0} mesaj).`);
            await loadInbox();
        });
    }

    async function handleEtiketKaydet() {
        if (!aktifKonusma || !etiketMetin.trim()) {
            toast.error("Görünen ad / etiket yazın.");
            return;
        }
        await runBusy("inbox-etiket", async () => {
            await whatsappMerkez.inboxEtiket(konusmaKey(aktifKonusma), etiketMetin.trim());
            toast.success("Etiket kaydedildi.");
            await loadInbox();
        });
    }

    const sonGelenMesajId = useMemo(() => {
        if (!aktifKonusma?.mesajlar?.length) return 0;
        const gelen = [...aktifKonusma.mesajlar].reverse().find((m) => m.yon === "GELEN");
        return gelen?.id || 0;
    }, [aktifKonusma?.mesajlar]);

    const loadAkilTaslak = useCallback(async () => {
        if (!aktifKonusma || aktifKonusma.is_grup) {
            setAiTaslaklar([]);
            setAiMeta(null);
            return;
        }
        const sonGelen = [...(aktifKonusma.mesajlar || [])]
            .reverse()
            .find((m) => m.yon === "GELEN");
        const sonDosyaId =
            sonGelen?.sigorta_dosyasi_id ||
            [...(aktifKonusma.mesajlar || [])]
                .reverse()
                .find((m) => m.sigorta_dosyasi_id)?.sigorta_dosyasi_id;
        setAiLoading(true);
        try {
            const data = await whatsappMerkez.inboxAkilTaslak({
                telefon: konusmaKey(aktifKonusma),
                gelen_mesaj: sonGelen?.mesaj || undefined,
                muvekkil_id: aktifKonusma.muvekkil_id || undefined,
                sigorta_dosyasi_id: sonDosyaId || undefined,
            });
            setAiTaslaklar(data.taslaklar || []);
            setAiMeta(data);
        } catch {
            setAiTaslaklar([]);
        } finally {
            setAiLoading(false);
        }
    }, [aktifKonusma, konusmaKey]);

    useEffect(() => {
        if (aktifKonusma) loadAkilTaslak();
    }, [aktifKonusma, seciliKonusmaKey, aktifKonusma?.muvekkil_id, sonGelenMesajId, loadAkilTaslak]);

    async function handleKonusmaYanit() {
        if (!aktifKonusma) return;
        const mesaj = yanitMetin.trim();
        if (!mesaj) {
            toast.error("Mesaj yazın.");
            return;
        }
        await runBusy("inbox-yanit", async () => {
            shouldStickBottomRef.current = true;
            const res = await whatsappMerkez.inboxKonusmaYanit(konusmaKey(aktifKonusma), mesaj);
            if (res.ok) {
                toast.success("Yanıt gönderildi.");
                setYanitMetin("");
                await loadInbox();
            } else {
                toast.error(res.detail || "Gönderilemedi.");
                if (res.wa_me_url) window.open(res.wa_me_url, "_blank");
            }
        });
    }

    async function handleInboxBagla(muvekkilId) {
        if (!aktifKonusma?.mesajlar?.length) return;
        const mid = (muvekkilId || "").trim();
        if (!mid) {
            toast.error("Müvekkil ID girin.");
            return;
        }
        const lastId = aktifKonusma.mesajlar[aktifKonusma.mesajlar.length - 1].id;
        try {
            await whatsappMerkez.inboxBagla(lastId, { muvekkil_id: Number(mid) });
            toast.success("Müvekkile bağlandı.");
            await loadInbox();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bağlanamadı.");
        }
    }

    const tumSecili =
        aktifKonusma?.mesajlar?.length > 0 &&
        aktifKonusma.mesajlar.every((m) => seciliMesajlar[m.id]);

    const mesajGruplari = useMemo(() => {
        const items = [];
        let lastDate = "";
        (aktifKonusma?.mesajlar || []).forEach((m) => {
            const dateKey = (m.olusturma_tarihi || "").slice(0, 10);
            if (dateKey !== lastDate) {
                items.push({ type: "date", key: `d-${dateKey}`, label: formatDateSep(m.olusturma_tarihi) });
                lastDate = dateKey;
            }
            items.push({ type: "msg", key: `m-${m.id}`, data: m });
        });
        return items;
    }, [aktifKonusma?.mesajlar]);

    return (
        <div className={`wa-inbox ${mobileChatOpen ? "wa-inbox--chat-open" : ""} ${crmOpen ? "wa-inbox--crm-open" : ""}`}>
            {onayBekleyenToplam > 0 && (
                <div className="wa-inbox__onay-band">
                    <span className="flex items-center gap-2 text-sm">
                        <ClipboardCheck className="h-4 w-4 text-amber-300" />
                        {onayBekleyenToplam} evrak onay bekliyor
                        <span className="text-[11px] text-[#8696a0]">· Ctrl+S</span>
                    </span>
                    <button
                        type="button"
                        className="wa-inbox__onay-band-btn"
                        onClick={() => setOnayDrawerOpen(true)}
                    >
                        Onay çekmecesini aç
                    </button>
                </div>
            )}
            <div className="wa-inbox__layout wa-inbox__layout--premium">
            <aside className="wa-sidebar">
                <div className="wa-sidebar__top">
                    <div>
                        <div className="wa-sidebar__title flex items-center gap-2">
                            WhatsApp
                            {wsConnected ? (
                                <Wifi className="h-3.5 w-3.5 text-emerald-400" title="Canlı bağlantı" />
                            ) : (
                                <WifiOff className="h-3.5 w-3.5 text-amber-400" title="Polling modu" />
                            )}
                        </div>
                        <div className="wa-sidebar__count">{konusmalar.length} konuşma</div>
                    </div>
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={loadInbox}
                        className="h-8 w-8 p-0 text-[#8696a0] hover:text-white"
                    >
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                </div>
                <div className="wa-sidebar__filters">
                    <div className="wa-sidebar__search">
                        <Search className="h-3.5 w-3.5" />
                        <input
                            value={arama}
                            onChange={(e) => setArama(e.target.value)}
                            placeholder="Ara…"
                        />
                    </div>
                    <select value={filtre} onChange={(e) => setFiltre(e.target.value)}>
                        <option value="tumu">Tüm konuşmalar</option>
                        <option value="okunmamis">Okunmamış</option>
                        <option value="sla">SLA ihlali</option>
                        <option value="acil">Acil</option>
                        <option value="bana">Bana atanan</option>
                        <option value="gruplar">Gruplar</option>
                        <option value="baglanmamis">Bağlanmamış</option>
                        <option value="cozuldu">Çözüldü</option>
                    </select>
                </div>
                <div className="wa-sidebar__list">
                    {filtreliKonusmalar.length === 0 ? (
                        <p className="text-sm text-[#8696a0] p-6 text-center">Konuşma yok.</p>
                    ) : (
                        filtreliKonusmalar.map((k) => {
                            const key = konusmaKey(k);
                            const unread = k.okunmamis > 0;
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => secKonusma(key)}
                                    className={`wa-chat-item ${seciliKonusmaKey === key ? "wa-chat-item--active" : ""}`}
                                >
                                    <div
                                        className={`wa-avatar ${k.is_grup ? "wa-avatar--group" : ""}`}
                                        aria-hidden
                                    >
                                        {k.is_grup ? <Users className="h-5 w-5" /> : avatarInitials(konusmaBaslik(k))}
                                    </div>
                                    <div className="wa-chat-item__body">
                                        <div className="wa-chat-item__row">
                                            <span className="wa-chat-item__name">{konusmaBaslik(k)}</span>
                                            <span
                                                className={`wa-chat-item__time ${unread ? "wa-chat-item__time--unread" : ""}`}
                                            >
                                                {formatListTime(k.son_tarih)}
                                            </span>
                                        </div>
                                        <div className="wa-chat-item__row">
                                            <span
                                                className={`wa-chat-item__preview ${unread ? "wa-chat-item__preview--unread" : ""}`}
                                            >
                                                {k.son_mesaj || "—"}
                                            </span>
                                            <span className="wa-chat-item__badges">
                                                {k.sla_ihlali ? <span className="wa-badge wa-badge--sla">SLA</span> : null}
                                                {k.oncelik === "ACIL" ? <span className="wa-badge wa-badge--acil">!</span> : null}
                                                {unread ? <span className="wa-badge">{k.okunmamis}</span> : null}
                                            </span>
                                        </div>
                                    </div>
                                </button>
                            );
                        })
                    )}
                </div>
            </aside>

            <main className="wa-chat">
                {!aktifKonusma ? (
                    <div className="wa-empty">
                        <MessageSquare className="h-16 w-16" />
                        <p>WhatsApp Web — sol listeden bir konuşma seçin</p>
                    </div>
                ) : (
                    <>
                        <div className="wa-chat__header">
                            <div className="wa-chat__header-info">
                                <button
                                    type="button"
                                    className="wa-back-btn"
                                    onClick={() => setMobileChatOpen(false)}
                                    aria-label="Geri"
                                >
                                    <ArrowLeft className="h-5 w-5" />
                                </button>
                                <div
                                    className={`wa-avatar ${aktifKonusma.is_grup ? "wa-avatar--group" : ""}`}
                                    style={{ width: 40, height: 40, fontSize: 14 }}
                                >
                                    {aktifKonusma.is_grup ? (
                                        <Users className="h-4 w-4" />
                                    ) : (
                                        avatarInitials(konusmaBaslik(aktifKonusma))
                                    )}
                                </div>
                                <div className="wa-chat__header-text">
                                    <h3>{konusmaBaslik(aktifKonusma)}</h3>
                                    <p>
                                        {aktifKonusma.is_grup
                                            ? "WhatsApp grubu"
                                            : aktifKonusma.telefon_formatli}{" "}
                                        · {aktifKonusma.mesaj_sayisi} mesaj
                                    </p>
                                </div>
                            </div>
                            <div className="wa-chat__header-actions">
                                <button type="button" className="wa-crm-toggle" onClick={() => setCrmOpen((v) => !v)} title="CRM panel">
                                    CRM
                                </button>
                                <button type="button" onClick={() => setShowTools((v) => !v)} title="Araçlar">
                                    <MoreVertical className="h-4 w-4 inline" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSelectMode((v) => !v);
                                        setSeciliMesajlar({});
                                    }}
                                >
                                    {selectMode ? "Seçimi kapat" : "Seç"}
                                </button>
                                <button type="button" className="danger" onClick={handleKonusmaSil}>
                                    Sil
                                </button>
                            </div>
                        </div>

                        {aktifKonusma.muvekkil_eslesme_uyari && (
                            <div className="wa-alert">{aktifKonusma.muvekkil_eslesme_uyari}</div>
                        )}

                        {showTools && !aktifKonusma.is_grup && (
                            <div className="wa-chat__tools wa-chat__tools--stack">
                                <input
                                    value={etiketMetin}
                                    onChange={(e) => setEtiketMetin(e.target.value)}
                                    placeholder="Görünen ad"
                                    className="min-w-[120px] flex-1"
                                />
                                <button type="button" onClick={handleEtiketKaydet}>
                                    Etiket kaydet
                                </button>
                                <input
                                    placeholder="Müvekkil ID"
                                    value={baglaMuvekkil[konusmaKey(aktifKonusma)] || ""}
                                    onChange={(e) =>
                                        setBaglaMuvekkil((p) => ({
                                            ...p,
                                            [konusmaKey(aktifKonusma)]: e.target.value,
                                        }))
                                    }
                                    className="w-24"
                                />
                                <button
                                    type="button"
                                    onClick={() => handleInboxBagla(baglaMuvekkil[konusmaKey(aktifKonusma)])}
                                >
                                    Bağla
                                </button>
                                {aktifKonusma.konusma_baglantisi?.dosya_no && (
                                    <span className="text-[11px] text-emerald-300/90 inline-flex items-center gap-1">
                                        <Pin className="h-3 w-3" />
                                        Varsayılan: {aktifKonusma.konusma_baglantisi.dosya_no}
                                    </span>
                                )}
                                <input
                                    placeholder="Varsayılan dosya ID"
                                    value={varsayilanDosyaId}
                                    onChange={(e) => setVarsayilanDosyaId(e.target.value)}
                                    className="w-28"
                                />
                                <label className="flex items-center gap-1 text-[11px] text-[#8696a0] cursor-pointer">
                                    <Checkbox
                                        checked={herZamanBuDosya}
                                        onCheckedChange={(v) => setHerZamanBuDosya(!!v)}
                                    />
                                    Her zaman bu dosya
                                </label>
                                <button type="button" onClick={handleVarsayilanDosyaKaydet}>
                                    Dosya hafızası
                                </button>
                                {aktifKonusma.muvekkil_otomatik && (
                                    <button type="button" onClick={handleEslestirmeKaldir}>
                                        <UserX className="h-3 w-3 inline mr-1" />
                                        Eşleşmeyi kaldır
                                    </button>
                                )}
                            </div>
                        )}

                        {selectMode && (
                            <div className="wa-chat__tools">
                                <label className="flex items-center gap-2 text-xs text-[#8696a0] cursor-pointer">
                                    <Checkbox checked={tumSecili} onCheckedChange={(v) => toggleTumMesajlar(!!v)} />
                                    <CheckSquare className="h-3 w-3" />
                                    Tümünü seç
                                </label>
                                {seciliIdler.length > 0 && (
                                    <button type="button" className="danger" onClick={handleSeciliSil}>
                                        <Trash2 className="h-3 w-3 inline mr-1" />
                                        Sil ({seciliIdler.length})
                                    </button>
                                )}
                            </div>
                        )}

                        <div
                            ref={messagesContainerRef}
                            className="wa-chat__messages"
                            onScroll={handleMessagesScroll}
                        >
                            {mesajGruplari.map((item) => {
                                if (item.type === "date") {
                                    return (
                                        <div key={item.key} className="wa-date-sep">
                                            {item.label}
                                        </div>
                                    );
                                }
                                const m = item.data;
                                const gelen = m.yon === "GELEN";
                                return (
                                    <div
                                        key={item.key}
                                        className={`wa-msg-row ${gelen ? "wa-msg-row--in" : "wa-msg-row--out"} ${selectMode ? "wa-msg-row--select" : ""}`}
                                    >
                                        {selectMode && (
                                            <Checkbox
                                                checked={!!seciliMesajlar[m.id]}
                                                onCheckedChange={(v) => toggleMesaj(m.id, !!v)}
                                                className="mt-2"
                                            />
                                        )}
                                        <div className={`wa-bubble ${gelen ? "wa-bubble--in" : "wa-bubble--out"}`}>
                                            {gelen && aktifKonusma.is_grup && (
                                                <div className="wa-bubble__sender">
                                                    {m.gonderen_etiket || m.gonderen_adi || "Katılımcı"}
                                                </div>
                                            )}
                                            <div className="wa-bubble__text">
                                                {m.mesaj || m.media_turu || "—"}
                                            </div>
                                            {m.media_turu && (
                                                <div className="wa-bubble__media-actions">
                                                    <button
                                                        type="button"
                                                        className="wa-bubble__preview-btn"
                                                        onClick={() => setEvrakOnizleme({ inboxId: m.id, mesaj: m })}
                                                    >
                                                        <FileText className="h-3 w-3" /> Önizle
                                                    </button>
                                                </div>
                                            )}
                                            {m.media_turu && (
                                                <div className="mt-1.5 text-[10px] leading-snug">
                                                    {m.evrak_otomasyon_durumu === "YUKLENDI" ? (
                                                        <span className="inline-flex flex-wrap items-center gap-1 text-emerald-400/90">
                                                            <FileText className="h-3 w-3" />
                                                            {m.evrak_onay_durumu === "ONAYLI"
                                                                ? "Evrak onaylı yüklendi"
                                                                : "Evrak yüklendi (onay bekliyor)"}
                                                            {m.evrak_turu_label ? ` · ${m.evrak_turu_label}` : ""}
                                                            {m.dosya_no ? ` · ${m.dosya_no}` : ""}
                                                            {m.evrak_otomasyon_notu ? (
                                                                <span className="block w-full text-[9px] text-[#8696a0] mt-0.5">
                                                                    {m.evrak_otomasyon_notu}
                                                                </span>
                                                            ) : null}
                                                        </span>
                                                    ) : m.evrak_otomasyon_durumu === "ONAY_BEKLIYOR" ? (
                                                        <span className="block text-amber-300/95">
                                                            <FileText className="h-3 w-3 inline mr-1" />
                                                            Onay kuyruğunda
                                                            {m.evrak_eslestirme?.dosya_no
                                                                ? ` · ${m.evrak_eslestirme.dosya_no}`
                                                                : ""}
                                                            {m.evrak_eslestirme?.guven_puani != null
                                                                ? ` · güven %${m.evrak_eslestirme.guven_puani}`
                                                                : ""}
                                                            <button
                                                                type="button"
                                                                className="ml-2 underline hover:text-white"
                                                                onClick={() => {
                                                                    setOnayDrawerInboxId(m.id);
                                                                    setOnayDrawerOpen(true);
                                                                }}
                                                            >
                                                                İncele
                                                            </button>
                                                        </span>
                                                    ) : m.evrak_otomasyon_durumu === "REDDEDILDI" ? (
                                                        <span className="block text-rose-400/90">
                                                            Reddedildi
                                                            {m.evrak_otomasyon_notu ? ` · ${m.evrak_otomasyon_notu}` : ""}
                                                        </span>
                                                    ) : m.evrak_otomasyon_durumu === "HATA" ||
                                                      m.evrak_otomasyon_durumu === "ATLANDI" ? (
                                                        <span className="block text-amber-400/90">
                                                            {m.evrak_otomasyon_notu ||
                                                                "Evrak işlenemedi."}
                                                            {gelen && (
                                                                <button
                                                                    type="button"
                                                                    className="ml-2 underline hover:text-white"
                                                                    onClick={() => evrakYukle(m.id)}
                                                                >
                                                                    Tekrar dene
                                                                </button>
                                                            )}
                                                        </span>
                                                    ) : gelen ? (
                                                        <button
                                                            type="button"
                                                            className="text-sky-300/90 underline hover:text-white inline-flex items-center gap-1"
                                                            onClick={() => evrakYukle(m.id)}
                                                        >
                                                            <FileText className="h-3 w-3" />
                                                            Dosyaya evrak olarak yükle
                                                        </button>
                                                    ) : null}
                                                </div>
                                            )}
                                            <div className="wa-bubble__meta">
                                            <span className="wa-bubble__time">{formatBubbleTime(m.olusturma_tarihi || m.tarih || m.created_at)}</span>
                                            {!gelen ? (
                                                <span className={`wa-tick ${(m.durum === 'GONDERILDI' || m.okundu) ? 'wa-tick--read' : ''}`}>
                                                    {(m.durum === 'GONDERILDI' || m.okundu) ? '✓✓' : '✓'}
                                                </span>
                                            ) : null}
                                        </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {!aktifKonusma.is_grup && aiMeta?.niyet_label ? (
                            <div className="wa-chat__intent">
                                <Brain className="h-3 w-3" /> Niyet: {aiMeta.niyet_label}
                            </div>
                        ) : null}

                        <WhatsAppInboxComposer
                            konusmaKey={seciliKonusmaKey}
                            konusma={aktifKonusma}
                            yanitMetin={yanitMetin}
                            setYanitMetin={setYanitMetin}
                            onSend={handleKonusmaYanit}
                            busy={!!busyKey}
                            aiTaslaklar={aiTaslaklar}
                            aiLoading={aiLoading}
                            onLoadAkil={loadAkilTaslak}
                        />
                    </>
                )}
            </main>

            <WhatsAppInboxCrmPanel
                konusmaKey={seciliKonusmaKey}
                konusma={aktifKonusma}
                onMetaUpdated={() => loadInbox().catch(() => {})}
            />
            </div>

            {/* Evrak Manuel İnceleme Modal */}
            {evrakInceleme && createPortal(
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
                    <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0F1629] p-6 text-slate-200">
                        <div className="mb-4 flex items-center gap-3">
                            <FileText className="h-6 w-6 text-amber-400" />
                            <div>
                                <div className="text-lg font-semibold">Evrak Manuel İnceleme</div>
                                <div className="text-xs text-slate-400">Otomatik yükleme güvenlik nedeniyle engellendi</div>
                            </div>
                        </div>

                        <div className="mb-4 rounded-xl border border-white/10 bg-black/30 p-4 text-sm">
                            <div className="mb-2 text-rose-300">Neden yüklenemedi?</div>
                            <div className="text-slate-300">{evrakInceleme.atlaNeden || evrakInceleme.detail}</div>
                        </div>

                        <div className="mb-4 text-xs text-slate-400">
                            {evrakInceleme.manualUploadRequired
                                ? "WhatsApp medyası sunucudan indirilemedi. Belgeyi bilgisayarınızdan seçerek yükleyin."
                                : "Bu evrakı yüklemek için müvekkil/dosya bağlantısını kontrol edin veya manuel olarak onaylayın. Sistem plaka, dosya no veya TC doğrulaması yapamadı."}
                        </div>

                        {evrakInceleme.manualUploadRequired ? (
                            <div className="mb-4 space-y-2">
                                <label className="text-xs font-medium text-slate-300">
                                    Belge dosyası (PDF / fotoğraf)
                                </label>
                                <input
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png,.webp,image/*,application/pdf"
                                    className="w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm text-slate-100 file:mr-3 file:rounded file:border-0 file:bg-amber-600 file:px-3 file:py-1 file:text-white"
                                    onChange={(e) => setManuelEvrakDosya(e.target.files?.[0] || null)}
                                />
                                {manuelEvrakDosya ? (
                                    <div className="text-xs text-emerald-300">{manuelEvrakDosya.name}</div>
                                ) : null}
                            </div>
                        ) : null}

                        <div className="mb-4 space-y-2">
                            <label className="text-xs font-medium text-slate-300">Dosya ara (no / plaka / müvekkil)</label>
                            <input
                                type="search"
                                className="w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm text-slate-100"
                                placeholder="MBS-… veya plaka"
                                value={dosyaArama}
                                onChange={async (e) => {
                                    const q = e.target.value;
                                    setDosyaArama(q);
                                    if (q.trim().length < 2) {
                                        setDosyaAdaylar([]);
                                        return;
                                    }
                                    try {
                                        const res = await crud.list("sigorta-dosyalari", {
                                            search: q.trim(),
                                            page_size: 8,
                                        });
                                        setDosyaAdaylar(res.results || res || []);
                                    } catch {
                                        setDosyaAdaylar([]);
                                    }
                                }}
                            />
                            {dosyaAdaylar.length > 0 && (
                                <select
                                    className="w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm text-slate-100"
                                    value={seciliDosyaId}
                                    onChange={(e) => setSeciliDosyaId(e.target.value)}
                                >
                                    <option value="">Dosya seçin…</option>
                                    {dosyaAdaylar.map((d) => (
                                        <option key={d.id} value={d.id}>
                                            {(d.dosya_no || `#${d.id}`) +
                                                (d.plaka ? ` · ${d.plaka}` : "") +
                                                (d.muvekkil_ad_soyad ? ` · ${d.muvekkil_ad_soyad}` : "")}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>

                        <div className="flex flex-wrap gap-2">
                            <Button
                                variant="outline"
                                className="border-white/20 text-slate-300"
                                onClick={() => {
                                    setEvrakInceleme(null);
                                    setDosyaArama("");
                                    setDosyaAdaylar([]);
                                    setSeciliDosyaId("");
                                    setManuelEvrakDosya(null);
                                }}
                            >
                                Kapat
                            </Button>
                            <Button
                                className="bg-amber-600 text-white hover:bg-amber-500"
                                disabled={
                                    manuelYukleme ||
                                    !seciliDosyaId ||
                                    !evrakInceleme?.inboxId ||
                                    (evrakInceleme?.manualUploadRequired && !manuelEvrakDosya)
                                }
                                onClick={async () => {
                                    if (!evrakInceleme?.inboxId || !seciliDosyaId) return;
                                    if (evrakInceleme.manualUploadRequired && !manuelEvrakDosya) {
                                        toast.error("Lütfen yüklenecek belge dosyasını seçin.");
                                        return;
                                    }
                                    setManuelYukleme(true);
                                    try {
                                        await whatsappMerkez.inboxBagla(evrakInceleme.inboxId, {
                                            dosya_id: Number(seciliDosyaId),
                                        });
                                        const payload = { dosya_id: Number(seciliDosyaId) };
                                        if (manuelEvrakDosya) payload.file = manuelEvrakDosya;
                                        const res = await whatsappMerkez.inboxEvrakYukle(
                                            evrakInceleme.inboxId,
                                            payload,
                                        );
                                        if (res?.ok) {
                                            toast.success(res.detail || "Evrak dosyaya yüklendi.");
                                            setEvrakInceleme(null);
                                            setDosyaArama("");
                                            setDosyaAdaylar([]);
                                            setSeciliDosyaId("");
                                            setManuelEvrakDosya(null);
                                            loadInbox();
                                        } else {
                                            toast.error(res?.detail || "Yükleme başarısız.");
                                        }
                                    } catch (err) {
                                        toast.error(err?.response?.data?.detail || "Manuel yükleme hatası.");
                                    } finally {
                                        setManuelYukleme(false);
                                    }
                                }}
                            >
                                {manuelYukleme ? "Yükleniyor…" : "Seçili Dosyaya Yükle"}
                            </Button>
                            <Button
                                variant="destructive"
                                className="bg-rose-600 text-white hover:bg-rose-500"
                                onClick={async () => {
                                    // Atla — inbox kaydını "ATLANDI" olarak işaretle (opsiyonel)
                                    setEvrakInceleme(null);
                                    toast.info("Evrak manuel incelemeye bırakıldı.");
                                }}
                            >
                                Atla (İncelenecek)
                            </Button>
                        </div>
                    </div>
                </div>,
                document.body,
            )}

            <WhatsAppEvrakOnizlemeModal
                open={!!evrakOnizleme}
                inboxId={evrakOnizleme?.inboxId}
                mesaj={evrakOnizleme?.mesaj}
                onClose={() => setEvrakOnizleme(null)}
                onDone={loadInbox}
            />

            <WhatsAppInboxOnayDrawer
                open={onayDrawerOpen}
                initialInboxId={onayDrawerInboxId}
                onClose={() => {
                    setOnayDrawerOpen(false);
                    setOnayDrawerInboxId(null);
                }}
                onDone={loadInbox}
            />
        </div>
    );
}
